import { useState } from 'react';
import type { CommentNode } from '@/hooks/useComments';
import CommentForm from './CommentForm';
import CommentBody from './CommentBody';

const COLLAPSE_DEPTH = 3;

interface CommentThreadProps {
  node: CommentNode;
  currentUserId: number | undefined;
  isSuperadmin: boolean;
  onReply: (body: string, parentId: string) => Promise<void>;
  onEdit: (id: string, body: string) => Promise<void>;
  onDelete: (id: string) => Promise<void>;
  onUpvote: (id: string, hasUpvoted: boolean) => Promise<void>;
}

function timeAgo(iso: string): string {
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}


export default function CommentThread({
  node,
  currentUserId,
  isSuperadmin,
  onReply,
  onEdit,
  onDelete,
  onUpvote,
}: CommentThreadProps) {
  const [showReplyForm, setShowReplyForm] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [collapsed, setCollapsed] = useState(false);

  const isOwner = currentUserId !== undefined && node.author === currentUserId;
  const canEdit = isOwner && !node.is_deleted;
  const canDelete = (isOwner || isSuperadmin) && !node.is_deleted;
  const hasChildren = node.childNodes.length > 0;
  const deepChildren = node.depth >= COLLAPSE_DEPTH;

  const handleReply = async (body: string) => {
    await onReply(body, node.id);
    setShowReplyForm(false);
  };

  const handleEdit = async (body: string) => {
    await onEdit(node.id, body);
    setShowEditForm(false);
  };

  return (
    <div className={`${node.depth > 0 ? 'ml-6 border-l-2 border-gray-100 pl-4' : ''}`}>
      <div className="py-3">
        {/* Header */}
        <div className="flex items-center gap-2 mb-1 text-sm">
          <span className="font-medium text-gray-800">{node.author_name}</span>
          <span className="text-gray-400">{timeAgo(node.created_at)}</span>
          {node.is_edited && !node.is_deleted && (
            <span className="text-gray-300 text-sm">(edited)</span>
          )}
        </div>

        {/* Body */}
        {showEditForm ? (
          <CommentForm
            initialValue={node.body_display}
            onSubmit={handleEdit}
            onCancel={() => setShowEditForm(false)}
            submitLabel="Save"
            autoFocus
          />
        ) : node.is_deleted ? (
          <p className="text-sm text-gray-400 italic">{node.body_display}</p>
        ) : (
          <CommentBody text={node.body_display} />
        )}

        {/* Actions */}
        {!showEditForm && (
          <div className="flex items-center gap-3 mt-1.5">
            {/* Upvote */}
            <button
              onClick={() => onUpvote(node.id, node.has_upvoted)}
              className={`flex items-center gap-1 text-sm transition-colors ${
                node.has_upvoted ? 'text-rose-500' : 'text-gray-400 hover:text-rose-400'
              }`}
            >
              <span>{node.has_upvoted ? '♥' : '♡'}</span>
              {node.upvote_count > 0 && <span>{node.upvote_count}</span>}
            </button>

            {/* Reply */}
            {!node.is_deleted && (
              <button
                onClick={() => setShowReplyForm((v) => !v)}
                className="text-sm text-gray-400 hover:text-blue-500 transition-colors"
              >
                Reply
              </button>
            )}

            {/* Edit */}
            {canEdit && (
              <button
                onClick={() => setShowEditForm(true)}
                className="text-sm text-gray-400 hover:text-gray-600 transition-colors"
              >
                Edit
              </button>
            )}

            {/* Delete */}
            {canDelete && (
              <button
                onClick={() => {
                  if (window.confirm('Delete this comment?')) onDelete(node.id);
                }}
                className="text-sm text-gray-400 hover:text-red-500 transition-colors"
              >
                Delete
              </button>
            )}
          </div>
        )}

        {/* Reply form */}
        {showReplyForm && (
          <div className="mt-3">
            <CommentForm
              onSubmit={handleReply}
              onCancel={() => setShowReplyForm(false)}
              placeholder="Write a reply…"
              submitLabel="Reply"
              autoFocus
            />
          </div>
        )}
      </div>

      {/* Children */}
      {hasChildren && (
        <>
          {deepChildren && (
            <button
              onClick={() => setCollapsed((v) => !v)}
              className="text-sm text-blue-500 hover:underline mb-1"
            >
              {collapsed
                ? `Show ${node.childNodes.length} ${node.childNodes.length === 1 ? 'reply' : 'replies'}`
                : 'Hide replies'}
            </button>
          )}
          {(!deepChildren || !collapsed) &&
            node.childNodes.map((child) => (
              <CommentThread
                key={child.id}
                node={child}
                currentUserId={currentUserId}
                isSuperadmin={isSuperadmin}
                onReply={onReply}
                onEdit={onEdit}
                onDelete={onDelete}
                onUpvote={onUpvote}
              />
            ))}
        </>
      )}
    </div>
  );
}
